import React, { useState, ReactNode, Dispatch, SetStateAction } from 'react';
import { createContext, useContext } from 'use-context-selector';
import { ScheduleItemProps, EavSlotsProps } from '../models/Schedule';
import { Unit } from '../components/ServiceItem';
import { ProductItemProps } from '../models/Product';
import { PatientProps } from '../contexts/PatientContext';
import { PatientScheduleProps } from '../pages/Configurator';
import { ScheduleSpecialist } from '../models/ScheduleSpecialist';

export type UnitDate = {
  unitSghId: string;
  dates: string[];
};

export type UnitDatesAvailable = {
  scheduleId: string;
  unitDates: UnitDate[];
};

export type ScheduleContextProps = {
  scheduleItems: ScheduleItemProps[];
  selectedItem: ScheduleItemProps | undefined;
  setSelectedItem: Dispatch<SetStateAction<ScheduleItemProps | undefined>>;

  changeSchedule: (schedule: ScheduleItemProps) => ScheduleItemProps;
  clearUnits: () => void;
  addUnits: (units: Unit[]) => void;
  units: Unit[];
  removeSchedule: (productId: string) => void;
  clearSchedules: () => void;
  removeOneSchedule: (productId: string) => void;
  unitDatesAvailable: UnitDatesAvailable[];
  addUnitDatesAvailable: (unitTimes: UnitDatesAvailable) => void;
  findUnitDatesAvailable: (scheduleId: string, unitId: string) => string[];
  tokenSuccess?: string;
  setTokenSuccess?: Dispatch<SetStateAction<string>>;
  eavSlotsData: EavSlotsProps | undefined;
  setEavSlotsData: (eavSlots: EavSlotsProps) => void;
  changeProductSchedule: ({ schedule, product }: { schedule: ScheduleItemProps; product: ProductItemProps }) => void;
  getNotScheduledProducts: ({
    patient,
    productList
  }: {
    patient: PatientProps;
    productList: ProductItemProps[];
  }) => PatientScheduleProps[];
  scheduleSpecialistSelected: ScheduleSpecialist;
  setScheduleSpecialistSelected: React.Dispatch<React.SetStateAction<ScheduleSpecialist>>;
  changeScheduleSpecialist: (schedule: ScheduleSpecialist) => void;
  addSchedulesStore: (value: ScheduleItemProps[]) => void;
  setUnitDatesAvailable: (value: UnitDatesAvailable[]) => void;
};

type ScheduleProviderProps = {
  children: ReactNode;
};

export type ScheduleCardProps = {
  unit: Unit;
  schedule: ScheduleItemProps;
  patient: PatientProps;
  product: ProductItemProps;
};

export const ScheduleContext = createContext({} as ScheduleContextProps);

export function ScheduleProvider({ children }: ScheduleProviderProps) {
  const [scheduleItems, setScheduleItems] = useState<ScheduleItemProps[]>([]);
  const [selectedItem, setSelectedItem] = useState<ScheduleItemProps>();
  const [scheduleSpecialistSelected, setScheduleSpecialistSelected] = useState<ScheduleSpecialist>(
    {} as ScheduleSpecialist
  );

  const [units, setUnits] = useState<Unit[]>([]);
  const [eavSlotsData, setEavSlotsData] = useState<EavSlotsProps>();
  const [tokenSuccess, setTokenSuccess] = useState('');
  const [unitDatesAvailable, setUnitDatesAvailable] = React.useState<UnitDatesAvailable[]>([]);

  const changeProductSchedule = ({ schedule, product }: { schedule: ScheduleItemProps; product: ProductItemProps }) => {
    schedule.productIdLocal = product.idLocal;
    schedule.productId = product.id;
    schedule.price = product.price;
    schedule.discount = product.discount;
    schedule.productName = product.name;
    schedule.name = product.name;
    changeSchedule(schedule);
  };

  const changeSchedule = (schedule: ScheduleItemProps) => {
    setSelectedItem(sh => ({ ...sh, ...schedule }));
    setScheduleItems([schedule]);

    return schedule;
  };

  const changeScheduleSpecialist = (schedule: ScheduleSpecialist) => {
    setScheduleSpecialistSelected({
      ...scheduleSpecialistSelected,
      ...schedule
    });
  };

  const addUnits = (units: Unit[]) => {
    setUnits(units);
  };

  const clearUnits = () => {
    setUnits([]);
  };

  const removeSchedule = (productId: string) => {
    const newArraySchedule = scheduleItems.filter(schedule => schedule.productIdLocal !== productId);
    setScheduleItems(newArraySchedule);
  };

  const removeOneSchedule = (productId: string) => {
    const newArraySchedule = scheduleItems;
    let scheduleIndex = 0;
    scheduleItems.forEach((schedule, index) => {
      if (schedule.productId === productId && scheduleIndex === 0) {
        scheduleIndex = index;
      }
    });
    newArraySchedule.splice(scheduleIndex, 1);
    setScheduleItems(newArraySchedule);
  };

  const addUnitDatesAvailable = (unitDates: UnitDatesAvailable) => {
    const newUnitDatesAvailable = [] as UnitDatesAvailable[];

    if (!unitDatesAvailable.length) newUnitDatesAvailable.push(unitDates);

    unitDatesAvailable.forEach(unitDateAvailable => {
      if (unitDateAvailable.scheduleId === unitDates.scheduleId) {
        newUnitDatesAvailable.push({
          ...unitDates,
          unitDates: unitDates.unitDates
        });
      } else newUnitDatesAvailable.push(unitDateAvailable);
    });

    setUnitDatesAvailable(newUnitDatesAvailable);
  };

  const findUnitDatesAvailable = (scheduleId: string, unitId: string): string[] => {
    const scheduleUnitDates = unitDatesAvailable.find(unitDateAvailable => unitDateAvailable.scheduleId === scheduleId);

    if (scheduleUnitDates) {
      const unitDates = scheduleUnitDates.unitDates.find(item => item.unitSghId === unitId);
      if (unitDates) return unitDates.dates;
    }

    return [];
  };

  const getNotScheduledProducts = ({
    patient,
    productList
  }: {
    patient: PatientProps;
    productList: ProductItemProps[];
  }) => {
    const patientSchedules = [] as PatientScheduleProps[];
    scheduleItems.forEach(schedule => {
      if (schedule.idPatient === patient.idPatient) {
        const complete = !!schedule.time && !!schedule?.payment?.type && !!schedule.careType;

        if (!complete) {
          const patientSchedule = {} as PatientScheduleProps;
          patientSchedule.patient = patient;
          patientSchedule.payment = String(schedule?.payment?.type) || '';
          patientSchedule.schedule = schedule;
          patientSchedule.complete = complete;
          const relatedProduct = productList.find(p => p.id === schedule.productId);
          if (relatedProduct) {
            patientSchedule.product = relatedProduct;
          }
          patientSchedules.push(patientSchedule);
        }
      }
    });

    return patientSchedules;
  };

  const clearSchedules = () => {
    setScheduleItems([]);
    setSelectedItem({} as ScheduleItemProps);
    setUnits([]);
    setUnitDatesAvailable([]);
    setTokenSuccess('');
    setScheduleSpecialistSelected({} as ScheduleSpecialist);
  };

  const scheduleContextValue = React.useMemo(
    () => ({
      scheduleItems,
      selectedItem,
      setSelectedItem,
      changeSchedule,
      addUnits,
      clearUnits,
      units,
      removeSchedule,
      clearSchedules,
      removeOneSchedule,
      unitDatesAvailable,
      addUnitDatesAvailable,
      findUnitDatesAvailable,
      tokenSuccess,
      setTokenSuccess,
      eavSlotsData,
      setEavSlotsData,
      changeProductSchedule,
      getNotScheduledProducts,
      scheduleSpecialistSelected,
      setScheduleSpecialistSelected,
      changeScheduleSpecialist,
      addSchedulesStore: setScheduleItems,
      setUnitDatesAvailable
    }),
    [
      scheduleItems,
      selectedItem,
      setSelectedItem,
      changeSchedule,
      addUnits,
      clearUnits,
      units,
      removeSchedule,
      clearSchedules,
      removeOneSchedule,
      unitDatesAvailable,
      addUnitDatesAvailable,
      findUnitDatesAvailable,
      tokenSuccess,
      setTokenSuccess,
      eavSlotsData,
      setEavSlotsData,
      changeProductSchedule,
      getNotScheduledProducts,
      scheduleSpecialistSelected,
      setScheduleSpecialistSelected,
      changeScheduleSpecialist,
      setScheduleItems,
      setUnitDatesAvailable
    ]
  );

  return <ScheduleContext.Provider value={scheduleContextValue}>{children}</ScheduleContext.Provider>;
}

export function useScheduleContext() {
  return useContext(ScheduleContext);
}
